import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/**
 * CMS administration (spec §38, §39).
 *
 * The public read APIs have been live since Phase 6; this is the side that
 * writes them. Every endpoint is behind `cms.manage`, reads included — an admin
 * listing carries drafts, inactive banners and scheduled posts, none of which
 * the storefront endpoints return.
 */

export type ContentStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
export type BannerPlacement = 'HERO' | 'CATEGORY' | 'PROMOTIONAL' | 'MOBILE' | 'SIDEBAR';
/** What a visitor would see right now, which `isActive` alone cannot say. */
export type ScheduleState = 'SHOWING' | 'SCHEDULED' | 'EXPIRED' | 'INACTIVE';
export type PostState = 'DRAFT' | 'SCHEDULED' | 'LIVE' | 'ARCHIVED';

export interface Banner {
  id: string;
  title: string;
  subtitle: string | null;
  imageUrl: string;
  mobileImageUrl: string | null;
  altText: string | null;
  ctaText: string | null;
  ctaUrl: string | null;
  placement: BannerPlacement;
  startDate: string | null;
  endDate: string | null;
  priority: number;
  isActive: boolean;
  isShowing: boolean;
  scheduleState: ScheduleState;
  createdAt: string;
  updatedAt: string;
}

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  content?: string;
  coverImageUrl: string | null;
  status: ContentStatus;
  state: PostState;
  publishedAt: string | null;
  tags: string[];
  viewCount: number;
  metaTitle: string | null;
  metaDescription: string | null;
  author: { id: string; name: string } | null;
  createdAt: string;
  updatedAt: string;
}

export interface CmsPage {
  id: string;
  slug: string;
  title: string;
  content?: string;
  status: ContentStatus;
  metaTitle: string | null;
  metaDescription: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Faq {
  id: string;
  question: string;
  answer: string;
  category: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Testimonial {
  id: string;
  customerName: string;
  companyName: string | null;
  designation: string | null;
  content: string;
  rating: number;
  avatarUrl: string | null;
  displayOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
}

const keys = {
  all: ['admin', 'content'] as const,
  banners: (params: unknown) => [...keys.all, 'banners', params] as const,
  posts: (params: unknown) => [...keys.all, 'blog', params] as const,
  post: (id: string) => [...keys.all, 'blog', 'detail', id] as const,
  tags: () => [...keys.all, 'blog', 'tags'] as const,
  pages: (params: unknown) => [...keys.all, 'pages', params] as const,
  page: (id: string) => [...keys.all, 'pages', 'detail', id] as const,
  faqs: (params: unknown) => [...keys.all, 'faqs', params] as const,
  faqCategories: () => [...keys.all, 'faqs', 'categories'] as const,
  testimonials: (params: unknown) => [...keys.all, 'testimonials', params] as const,
};

/**
 * Every write invalidates the whole CMS key space.
 *
 * Coarse on purpose: these lists are small, and the alternative — invalidating
 * exactly the affected list — silently misses the counts and pickers that a
 * write also changes (a new FAQ category, a tag used for the first time).
 */
function useCmsMutation<TVariables, TResult>(mutationFn: (variables: TVariables) => Promise<TResult>) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.all });
    },
  });
}

/* --- Media ----------------------------------------------------------------- */

export interface UploadedMedia {
  url: string;
  fileName: string;
  size: number;
}

/** Uploads an image and returns its URL; the form being filled in stores it. */
export function useUploadMedia() {
  return useMutation({
    mutationFn: (file: File) => {
      const form = new FormData();
      form.append('file', file);

      return apiClient.upload<UploadedMedia>('/admin/content/media', form);
    },
  });
}

/* --- Banners --------------------------------------------------------------- */

export interface BannerParams extends ListParams {
  placement?: BannerPlacement;
  isActive?: boolean;
}

export function useBanners(params: BannerParams) {
  return useQuery({
    queryKey: keys.banners(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<Banner>>('/admin/content/banners', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          placement: params.placement,
          isActive: params.isActive,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useSaveBanner() {
  return useCmsMutation(({ id, ...body }: Partial<Banner> & { id?: string }) =>
    id
      ? apiClient.patch<Banner>(`/admin/content/banners/${id}`, body)
      : apiClient.post<Banner>('/admin/content/banners', body),
  );
}

export function useDeleteBanner() {
  return useCmsMutation((id: string) => apiClient.delete(`/admin/content/banners/${id}`));
}

/* --- Blog ------------------------------------------------------------------ */

export interface PostParams extends ListParams {
  status?: ContentStatus;
  tag?: string;
}

export function usePosts(params: PostParams) {
  return useQuery({
    queryKey: keys.posts(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<BlogPost>>('/admin/content/blog', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          status: params.status,
          tag: params.tag,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function usePost(postId: string | null) {
  return useQuery({
    queryKey: keys.post(postId ?? ''),
    queryFn: () => apiClient.get<BlogPost>(`/admin/content/blog/${postId}`),
    enabled: Boolean(postId),
  });
}

export function useBlogTags() {
  return useQuery({
    queryKey: keys.tags(),
    queryFn: () => apiClient.get<string[]>('/admin/content/blog/tags'),
    staleTime: 60_000,
  });
}

export function useSavePost() {
  return useCmsMutation(({ id, ...body }: Partial<BlogPost> & { id?: string }) =>
    id
      ? apiClient.patch<BlogPost>(`/admin/content/blog/${id}`, body)
      : apiClient.post<BlogPost>('/admin/content/blog', body),
  );
}

export function useDeletePost() {
  return useCmsMutation((id: string) => apiClient.delete(`/admin/content/blog/${id}`));
}

/* --- Pages ----------------------------------------------------------------- */

export interface PageParams extends ListParams {
  status?: ContentStatus;
}

export function usePages(params: PageParams) {
  return useQuery({
    queryKey: keys.pages(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<CmsPage>>('/admin/content/pages', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          status: params.status,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useCmsPage(pageId: string | null) {
  return useQuery({
    queryKey: keys.page(pageId ?? ''),
    queryFn: () => apiClient.get<CmsPage>(`/admin/content/pages/${pageId}`),
    enabled: Boolean(pageId),
  });
}

export function useSavePage() {
  return useCmsMutation(({ id, ...body }: Partial<CmsPage> & { id?: string }) =>
    id
      ? apiClient.patch<CmsPage>(`/admin/content/pages/${id}`, body)
      : apiClient.post<CmsPage>('/admin/content/pages', body),
  );
}

export function useDeletePage() {
  return useCmsMutation((id: string) => apiClient.delete(`/admin/content/pages/${id}`));
}

/* --- FAQs ------------------------------------------------------------------ */

export interface FaqParams extends ListParams {
  category?: string;
  isActive?: boolean;
}

export function useFaqs(params: FaqParams) {
  return useQuery({
    queryKey: keys.faqs(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<Faq>>('/admin/content/faqs', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          category: params.category,
          isActive: params.isActive,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useFaqCategories() {
  return useQuery({
    queryKey: keys.faqCategories(),
    queryFn: () => apiClient.get<string[]>('/admin/content/faqs/categories'),
    staleTime: 60_000,
  });
}

export function useSaveFaq() {
  return useCmsMutation(({ id, ...body }: Partial<Faq> & { id?: string }) =>
    id
      ? apiClient.patch<Faq>(`/admin/content/faqs/${id}`, body)
      : apiClient.post<Faq>('/admin/content/faqs', body),
  );
}

export function useDeleteFaq() {
  return useCmsMutation((id: string) => apiClient.delete(`/admin/content/faqs/${id}`));
}

/* --- Testimonials ---------------------------------------------------------- */

export interface TestimonialParams extends ListParams {
  isActive?: boolean;
}

export function useTestimonials(params: TestimonialParams) {
  return useQuery({
    queryKey: keys.testimonials(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<Testimonial>>('/admin/content/testimonials', {
        query: {
          page: params.page,
          limit: params.limit,
          search: params.search || undefined,
          isActive: params.isActive,
        },
      }),
    placeholderData: (previous) => previous,
  });
}

export function useSaveTestimonial() {
  return useCmsMutation(({ id, ...body }: Partial<Testimonial> & { id?: string }) =>
    id
      ? apiClient.patch<Testimonial>(`/admin/content/testimonials/${id}`, body)
      : apiClient.post<Testimonial>('/admin/content/testimonials', body),
  );
}

export function useDeleteTestimonial() {
  return useCmsMutation((id: string) => apiClient.delete(`/admin/content/testimonials/${id}`));
}

/* --- Labels ---------------------------------------------------------------- */

export const PLACEMENT_LABEL: Record<BannerPlacement, string> = {
  HERO: 'Hero',
  CATEGORY: 'Category',
  PROMOTIONAL: 'Promotional',
  MOBILE: 'Mobile',
  SIDEBAR: 'Sidebar',
};

export const PLACEMENTS = Object.keys(PLACEMENT_LABEL) as BannerPlacement[];

export const CONTENT_STATUSES: ContentStatus[] = ['DRAFT', 'PUBLISHED', 'ARCHIVED'];
